import { useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';

const TABS = [
  {
    to: '/',
    label: 'Today',
    icon: 'M3 11l9-8 9 8v9a2 2 0 0 1-2 2h-4v-6H9v6H5a2 2 0 0 1-2-2z',
  },
  { to: '/add', label: 'Add', icon: 'M12 5v14M5 12h14' },
  { to: '/trends', label: 'Trends', icon: 'M4 20V10M10 20V4M16 20v-7M22 20H2' },
  { to: '/weight', label: 'Weight', icon: 'M3 17l6-6 4 4 8-8' },
  {
    to: '/settings',
    label: 'Settings',
    icon: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
  },
] as const;

export function TabBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ref = useRef<HTMLElement>(null);

  // Published rather than assumed: the bar above this one needs to clear it,
  // and its real height depends on the border, the safe-area inset and
  // whatever the system font size does to the labels.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const publish = () =>
      document.documentElement.style.setProperty(
        '--tabbar-h',
        `${Math.round(el.getBoundingClientRect().height)}px`,
      );
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <nav
      ref={ref}
      aria-label="Main"
      className="bg-raised border-line fixed inset-x-0 bottom-0 z-20 border-t"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="mx-auto flex max-w-lg">
        {TABS.map(({ to, label, icon }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={to === '/'}
              onClick={(e) => {
                // Tapping Add while already on it should put the cursor in the
                // search field rather than doing nothing. A changing value is
                // what makes the add screen notice a repeat tap at all.
                if (to === '/add' && pathname === '/add') {
                  e.preventDefault();
                  navigate(`/add?focus=${Date.now()}`, { replace: true });
                }
              }}
              className={({ isActive }) =>
                `flex min-h-14 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
                  isActive ? 'text-accent' : 'text-faint'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <svg
                    viewBox="0 0 24 24"
                    className="size-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={isActive ? 2.2 : 1.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d={icon} />
                  </svg>
                  {label}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
